/**
 * Le droit à l'image : cinq autorisations, et ce qu'on en dit aux familles.
 *
 * ## Pourquoi « oui / non » et pas une case à cocher
 *
 * Une case décochée est ambiguë : on ne sait pas si la personne a refusé ou si
 * elle ne l'a pas vue. Six mois plus tard, devant un parent qui dit « je
 * n'ai jamais autorisé ça », l'ambiguïté est toujours tranchée contre nous —
 * et elle le mérite.
 *
 * Deux boutons obligent à répondre. « Non » devient alors une réponse, portée
 * par la famille, au lieu d'un silence porté par le formulaire.
 *
 * ## Pourquoi cinq questions et pas une
 *
 * Un consentement doit être spécifique (RGPD, art. 4.11). Un parent peut
 * vouloir que sa famille voie les photos de la semaine sans vouloir de son
 * enfant sur un flyer distribué dans les écoles — et ces deux positions sont
 * également raisonnables. Une case unique les confondait.
 *
 * ## Les réseaux sociaux ne posent qu'une question
 *
 * La politique de confidentialité (v0.1, §7) est catégorique : sur les réseaux
 * sociaux, Éclosion ne publie que des images où l'enfant est de dos ou n'est
 * pas facilement reconnaissable. Ce n'est donc pas un choix offert à la
 * famille, c'est une règle qu'on lui annonce — et la question D la porte dans
 * son intitulé.
 *
 * Un champ « sous quelle forme ? » ne pourrait jamais prendre qu'une valeur.
 * Le stocker par enfant ferait croire qu'il varie, et le premier filtre écrit
 * dessus mentirait.
 *
 * ## Ce que ce module ne décide pas
 *
 * Il décrit les usages et les traduit vers la base. Le texte affiché vit ici
 * parce qu'il doit être le même dans le formulaire de création, dans le
 * récapitulatif et dans la fiche de l'enfant : trois endroits où la question
 * est posée ou rappelée, et où un mot différent ferait douter de ce qui a été
 * accepté.
 */

/** Une réponse à une question d'autorisation. `null` = pas encore répondu. */
export type Reponse = 'oui' | 'non' | null;

export interface DroitImage {
  /** A. Espaces privés destinés aux familles. */
  espacesPrives: Reponse;
  /** B. Rapports d'activité et projets d'accueil publics. */
  rapports: Reponse;
  /** C. Site internet public d'Éclosion. */
  siteInternet: Reponse;
  /** D. Réseaux sociaux d'Éclosion, de dos ou non reconnaissable (politique §7). */
  reseauxSociaux: Reponse;
  /** E. Affiches, flyers et brochures. */
  supportsImprimes: Reponse;
  /** L'accord du second titulaire de l'autorité parentale. */
  accordAutreParent: boolean;
}

/** Les clés des cinq questions, dans l'ordre où elles sont posées. */
export const USAGES = [
  'espacesPrives',
  'rapports',
  'siteInternet',
  'reseauxSociaux',
  'supportsImprimes',
] as const;

export type CleUsage = (typeof USAGES)[number];

export interface DescriptionUsage {
  cle: CleUsage;
  lettre: string;
  titre: string;
  /** Ce que ça veut dire concrètement. Un exemple vaut mieux qu'une catégorie. */
  exemple: string;
  /** Vrai si l'usage sort de l'espace privé des familles. */
  public: boolean;
}

export const DESCRIPTIONS: DescriptionUsage[] = [
  {
    cle: 'espacesPrives',
    lettre: 'A',
    titre: 'Espaces privés destinés aux familles',
    exemple:
      "L'album de la semaine, accessible aux seules familles du groupe. Les photos y sont conservées un an.",
    public: false,
  },
  {
    cle: 'rapports',
    lettre: 'B',
    titre: 'Rapports d’activité et projets d’accueil publics',
    exemple:
      "Les documents remis à l'ONE, aux communes et aux organismes qui nous subsidient, et qui sont consultables par le public.",
    public: true,
  },
  {
    cle: 'siteInternet',
    lettre: 'C',
    titre: 'Site internet public d’Éclosion',
    exemple: 'Les pages des stages et des plaines, la page d’accueil.',
    public: true,
  },
  {
    cle: 'reseauxSociaux',
    lettre: 'D',
    titre: 'Réseaux sociaux d’Éclosion',
    exemple:
      'Notre page Facebook et notre compte Instagram. Nous n’y publions que des images où votre enfant est de dos ou n’est pas facilement reconnaissable.',
    public: true,
  },
  {
    cle: 'supportsImprimes',
    lettre: 'E',
    titre: 'Affiches, flyers et brochures',
    exemple:
      'Les supports imprimés distribués dans les écoles et les communes. Une fois imprimés, ils ne peuvent plus être retirés.',
    public: true,
  },
];

/** Une autorisation vierge : rien n'est pré-coché, et c'est le principe. */
export const DROIT_IMAGE_VIDE: DroitImage = {
  espacesPrives: null,
  rapports: null,
  siteInternet: null,
  reseauxSociaux: null,
  supportsImprimes: null,
  accordAutreParent: false,
};

/** Vrai si au moins un usage hors de l'espace privé des familles est autorisé. */
export function autorisePublication(droit: DroitImage): boolean {
  return DESCRIPTIONS.filter((d) => d.public).some((d) => droit[d.cle] === 'oui');
}

/**
 * Ce qui manque pour que les autorisations soient complètes.
 *
 * L'accord de l'autre parent n'est exigé QUE si une publication hors de
 * l'espace des familles est autorisée : demander sa signature pour un album
 * privé serait un obstacle sans objet.
 */
export function erreursDroitImage(droit: DroitImage): Record<string, string> {
  const erreurs: Record<string, string> = {};

  for (const d of DESCRIPTIONS) {
    if (droit[d.cle] === null) {
      erreurs[d.cle] = 'Merci de répondre par oui ou par non.';
    }
  }

  if (autorisePublication(droit) && !droit.accordAutreParent) {
    erreurs.accordAutreParent =
      "Une publication hors de l'espace des familles demande l'accord des deux parents.";
  }

  return erreurs;
}

/** La ligne `kid_image_rights` correspondante. */
export interface LigneDroitImage {
  kid_id: string;
  internal_spaces: boolean;
  activity_reports: boolean;
  public_website: boolean;
  social_media: boolean;
  print_materials: boolean;
  other_parent_agreement: boolean;
  policy_version: string;
  consented_at: string;
  consented_by: string | null;
  updated_at: string;
}

/**
 * Traduit les réponses du formulaire vers la ligne à écrire.
 *
 * Une réponse restée à `null` devient `false`. Ce n'est pas un raccourci : le
 * formulaire refuse d'aller plus loin tant qu'une question n'a pas de réponse,
 * donc ce cas ne se présente pas — et s'il se présentait, refuser est la seule
 * valeur qui ne prête pas à conséquence.
 */
export function versLigne(
  droit: DroitImage,
  kidId: string,
  versionPolitique: string,
  auteurId: string | null,
): Omit<LigneDroitImage, 'consented_at' | 'updated_at'> & { consented_at: string; updated_at: string } {
  const maintenant = new Date().toISOString();
  return {
    kid_id: kidId,
    internal_spaces: droit.espacesPrives === 'oui',
    activity_reports: droit.rapports === 'oui',
    public_website: droit.siteInternet === 'oui',
    social_media: droit.reseauxSociaux === 'oui',
    print_materials: droit.supportsImprimes === 'oui',
    other_parent_agreement: droit.accordAutreParent,
    policy_version: versionPolitique,
    consented_at: maintenant,
    consented_by: auteurId,
    updated_at: maintenant,
  };
}

/** Et la lecture inverse, pour rouvrir des autorisations déjà enregistrées. */
export function depuisLigne(ligne: Partial<LigneDroitImage> | null | undefined): DroitImage {
  if (!ligne) return { ...DROIT_IMAGE_VIDE };
  const ouiNon = (v: boolean | undefined): Reponse => (v === undefined ? null : v ? 'oui' : 'non');
  return {
    espacesPrives: ouiNon(ligne.internal_spaces),
    rapports: ouiNon(ligne.activity_reports),
    siteInternet: ouiNon(ligne.public_website),
    reseauxSociaux: ouiNon(ligne.social_media),
    supportsImprimes: ouiNon(ligne.print_materials),
    accordAutreParent: ligne.other_parent_agreement ?? false,
  };
}

/** Un résumé d'une ligne, pour l'afficher en une phrase. */
export function resume(droit: DroitImage): string {
  const oui = DESCRIPTIONS.filter((d) => droit[d.cle] === 'oui');
  if (oui.length === 0) return 'Aucune utilisation autorisée';
  if (oui.length === DESCRIPTIONS.length) return 'Tous les usages autorisés';
  return oui.map((d) => d.titre).join(' · ');
}
